import { startCase, camelCase } from "lodash";
import { useMemo } from "react";
import { Flex } from "@chakra-ui/react";
import CDS from "design-system";
import { FieldDef, IssueDetail } from "models/issue";
import { titleize } from "helpers/string";
import { ResolutionInfo } from "models/resolutions";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { toRoleFieldNameSafe } from "features/Issues/helpers";
import UserFormFields from "features/Issues/Components/UserFormFields";
import { UserUpdateFormValues } from "components/Forms/UserUpdateForm";

export function deactivatedRecipientIssueType(issue: IssueDetail) {
  return `Deactivated ${startCase(camelCase(issue?.context?.roleName || "Recipient"))}`;
}

export function IssueModalInputFields({ issue }: { issue: IssueDetail }) {
  const roleName = issue?.context?.roleName;
  const roleFieldName = toRoleFieldNameSafe(roleName) as keyof UserUpdateFormValues | undefined;
  const { data: newHireJourney } = useNewHireJourneyDetailQuery(issue?.newHireJourneyId, {
    enabled: !!issue?.newHireJourneyId && !!roleFieldName,
    staleTime: 0,
  });
  const userId = newHireJourney?.newHireId;
  const fields: FieldDef<UserUpdateFormValues>[] = useMemo(
    () => (roleFieldName ? [{ name: roleFieldName, label: `Reassign ${roleName ? titleize(roleName) : "Role"}` }] : []),
    [roleFieldName, roleName],
  );

  if (roleFieldName && userId) {
    return (
      <Flex direction="column" gap="1">
        <UserFormFields userId={userId} fieldsDefs={fields} issue={issue} />
      </Flex>
    );
  }

  const name = issue?.user?.fullName || "The recipient";
  return (
    <CDS.Text14>
      {`${name}'s account has been deactivated, so this will not be delivered. Reassign the ${
        roleName ? titleize(roleName) : "recipient"
      } on the affected actions, or reactivate the account.`}
    </CDS.Text14>
  );
}

export function deactivatedRecipientResolution(issue: IssueDetail): ResolutionInfo {
  const roleName = issue?.context?.roleName;
  const displayName = roleName ? titleize(roleName) : issue?.user?.fullName || "Recipient";
  return {
    issueType: "deactivated_recipient",
    tagText: deactivatedRecipientIssueType(issue),
    title: `${displayName} deactivated`,
    additionalContext: `Some actions will be sent to or assigned to the ${
      roleName ? titleize(roleName) : "recipient"
    }, but their account has been deactivated.`,
    inputComponent: IssueModalInputFields,
  };
}
